import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, MultiSelect, SegmentedControl, Select, Text } from "@mantine/core"
import type { TRecordingMode } from "../interfaces"
import type { IProps } from "./interfaces"

/**
 * Pre-recording streaming configuration: mode toggle (live transcript vs
 * batch), language MultiSelect, and the optional adapter selector. Also
 * surfaces the live-transcription badge / reconnect alert during recording.
 */
export const StreamingControlsPanel = ({
  state,
  mode,
  streamingAvailable,
  wsReconnecting,
  languages,
  streamingAdapterList,
  selectedBackend,
  defaultAdapterName,
  onModeChange,
  onLanguagesChange,
  onBackendChange,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const isActive = state === "recording" || state === "paused"
  const isFinalizing = state === "finalizing"
  const isStreaming = mode === "streaming"

  const defaultAdapterLabel =
    streamingAdapterList.find((a) => a.name === defaultAdapterName)?.label ?? defaultAdapterName

  return (
    <>
      {streamingAvailable && !isActive && !isFinalizing && (
        <SegmentedControl
          value={mode}
          onChange={(val) => onModeChange(val as TRecordingMode)}
          data={[
            { value: "streaming", label: i18n._(msg`Live transcript`) },
            { value: "batch", label: i18n._(msg`Batch`) },
          ]}
          size="xs"
          mb="sm"
        />
      )}

      {isActive && isStreaming && !wsReconnecting && (
        <Badge size="xs" variant="dot" color="success" mb="xs">
          Live Transcription
        </Badge>
      )}
      {isActive && isStreaming && wsReconnecting && (
        <Alert color="warning" variant="light" mb="xs" py="xs">
          <Text size="xs">
            <Trans>Reconnecting live transcription… audio is still recording.</Trans>
          </Text>
        </Alert>
      )}

      {isStreaming && !isActive && !isFinalizing && (
        <MultiSelect
          value={languages}
          onChange={onLanguagesChange}
          data={[
            { value: "nl", label: i18n._(msg`Dutch`) },
            { value: "en", label: i18n._(msg`English`) },
            { value: "de", label: i18n._(msg`German`) },
            { value: "fr", label: i18n._(msg`French`) },
            { value: "es", label: i18n._(msg`Spanish`) },
            { value: "it", label: i18n._(msg`Italian`) },
            { value: "pt", label: i18n._(msg`Portuguese`) },
          ]}
          placeholder={i18n._(msg`Auto-detect all languages`)}
          clearable
          size="xs"
          mb="sm"
        />
      )}

      {isStreaming && !isActive && !isFinalizing && streamingAdapterList.length > 1 && (
        <Select
          value={selectedBackend ?? ""}
          onChange={(val) => onBackendChange(val === "" ? null : val)}
          data={[
            { value: "", label: `Default (${defaultAdapterLabel})` },
            ...streamingAdapterList.map((a) => ({ value: a.name, label: a.label })),
          ]}
          size="xs"
          mb="sm"
        />
      )}
    </>
  )
}
